/**
 * Shared Access Service
 * 
 * Service for managing account sharing - inviting users, revoking access,
 * and managing shared access relationships.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from './firebase';
import { getApp } from 'firebase/app';

// Types - defined locally to avoid module path issues
export type SharedAccessLevel = 'read_only' | 'normal_user';

// Lazily get functions instance
const getFunctionsInstance = () => getFunctions(getApp(), 'us-central1');

// Response types
interface SharedUser {
  id: string;
  email: string;
  accessLevel: SharedAccessLevel;
  status: 'pending' | 'accepted';
  invitedAt?: string;
  acceptedAt?: string;
  expiresAt?: string;
}

export interface SharedAccountInfo {
  id: string;
  accountId: string;
  accountName: string;
  ownerEmail: string;
  accessLevel: SharedAccessLevel;
  acceptedAt?: string;
}

interface InviteResult {
  success: boolean;
  inviteId: string;
  message: string;
}

interface SharedUsersResult {
  users: SharedUser[];
}

interface SharedAccountsResult {
  sharedAccounts: SharedAccountInfo[];
}

export class SharedAccessService {
  private static instance: SharedAccessService;

  private constructor() { }

  public static getInstance(): SharedAccessService {
    if (!SharedAccessService.instance) {
      SharedAccessService.instance = new SharedAccessService();
    }
    return SharedAccessService.instance;
  }

  /**
   * Invite a user to access the current user's account
   */
  async inviteUser(email: string, accessLevel: SharedAccessLevel): Promise<InviteResult> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const functions = getFunctionsInstance();
      const inviteUserToAccount = httpsCallable<
        { inviteeEmail: string; accessLevel: SharedAccessLevel },
        InviteResult
      >(functions, 'inviteUserToAccount');

      const result = await inviteUserToAccount({
        inviteeEmail: email,
        accessLevel,
      });

      return result.data;
    } catch (error: any) {
      console.error('Error inviting user:', error);

      // Handle Firebase function errors
      if (error.code === 'functions/already-exists') {
        throw new Error('This user already has access or a pending invitation');
      }
      if (error.code === 'functions/permission-denied') {
        throw new Error('Only account owners can invite users');
      }

      throw new Error(error.message || 'Failed to send invitation');
    }
  }

  /**
   * Get list of users with shared access to the current user's account
   */
  async getSharedUsers(): Promise<SharedUser[]> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const functions = getFunctionsInstance();
      const getSharedUsersFn = httpsCallable<void, SharedUsersResult>(
        functions,
        'getSharedUsers'
      );

      const result = await getSharedUsersFn();
      return result.data.users || [];
    } catch (error: any) {
      console.error('Error getting shared users:', error);
      throw new Error(error.message || 'Failed to get shared users');
    }
  }

  /**
   * Update access level for a shared user
   */
  async updateAccessLevel(inviteId: string, accessLevel: SharedAccessLevel): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const functions = getFunctionsInstance();
      const updateSharedAccessFn = httpsCallable<
        { inviteId: string; accessLevel: SharedAccessLevel },
        { success: boolean }
      >(functions, 'updateSharedAccess');

      await updateSharedAccessFn({ inviteId, accessLevel });
    } catch (error: any) {
      console.error('Error updating access level:', error);
      throw new Error(error.message || 'Failed to update access level');
    }
  }

  /**
   * Revoke access for a shared user
   */
  async revokeAccess(inviteId: string): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const functions = getFunctionsInstance();
      const revokeSharedAccessFn = httpsCallable<
        { inviteId: string },
        { success: boolean }
      >(functions, 'revokeSharedAccess');

      await revokeSharedAccessFn({ inviteId });
    } catch (error: any) {
      console.error('Error revoking access:', error);
      throw new Error(error.message || 'Failed to revoke access');
    }
  }

  /**
   * Get accounts that have been shared with the current user
   */
  async getMySharedAccess(): Promise<SharedAccountInfo[]> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const functions = getFunctionsInstance();
      const getMySharedAccessFn = httpsCallable<void, SharedAccountsResult>(
        functions,
        'getMySharedAccess'
      );

      const result = await getMySharedAccessFn();
      return result.data.sharedAccounts || [];
    } catch (error: any) {
      console.error('Error getting shared access:', error);
      throw new Error(error.message || 'Failed to get shared accounts');
    }
  }
}

// Export singleton instance
export const sharedAccessService = SharedAccessService.getInstance();
